import { useReducer, useCallback, useEffect, useMemo } from 'react';
import { 
  MultiExtractionState, 
  MultiExtractionAction, 
  ExtractionSession, 
  ExtractionStatus 
} from '../types/multi-extraction';
import { ExtractionRequest, ExtractionProgressData, ExtractionResponse, SocketError } from '../types';

// Initial state
const initialState: MultiExtractionState = {
  sessions: {},
  activeSessionId: null,
  selectedSessionId: null,
};

// State reducer
function multiExtractionReducer(
  state: MultiExtractionState, 
  action: MultiExtractionAction
): MultiExtractionState {
  switch (action.type) {
    case 'START_EXTRACTION': {
      const { id, request, originalText, title } = action.payload;
      const newSession: ExtractionSession = {
        id,
        status: ExtractionStatus.IN_PROGRESS,
        originalText,
        request,
        progress: [],
        results: null,
        errors: [],
        startTime: new Date().toISOString(),
        codeColors: {},
        title: title || generateTitle(originalText),
      };
      
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: newSession,
        },
        activeSessionId: id,
        selectedSessionId: id, // Auto-select the new extraction
      };
    }

    case 'UPDATE_PROGRESS': {
      const { id, progress } = action.payload;
      const session = state.sessions[id];
      if (!session) return state;

      const updatedSession = {
        ...session,
        progress: [...session.progress, progress],
        status: progress.progress_percent >= 100 ? ExtractionStatus.COMPLETED : ExtractionStatus.IN_PROGRESS,
      };

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: updatedSession,
        },
      };
    }

    case 'COMPLETE_EXTRACTION': {
      const { id, results } = action.payload;
      const session = state.sessions[id];
      if (!session) return state;

      // Generate stable colors for codes if not already present
      const existingColors = session.codeColors || {};
      const codeColors = { ...existingColors };
      
      if (results.codes) {
        results.codes.forEach(code => {
          if (!codeColors[code.code]) {
            const hue = hashToHue(code.code);
            const saturation = 65;
            const lightness = 72;
            codeColors[code.code] = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          }
        });
      }

      const pcsFinalCodes = (results as any)?.pcs?.final_codes;
      if (Array.isArray(pcsFinalCodes)) {
        pcsFinalCodes.forEach((pcs: any) => {
          const code7 = String(pcs?.code7 || '').trim();
          const code4 = String(pcs?.code4 || '').trim();
          [code7, code4].filter(Boolean).forEach((c) => {
            if (!codeColors[c]) {
              const hue = hashToHue(c);
              const saturation = 65;
              const lightness = 72;
              codeColors[c] = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
            }
          });
        });
      }

      const updatedSession = {
        ...session,
        results,
        codeColors,
        status: ExtractionStatus.COMPLETED,
        endTime: new Date().toISOString(),
      };

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: updatedSession,
        },
        activeSessionId: state.activeSessionId === id ? null : state.activeSessionId,
      };
    }

    case 'ATTACH_BACKEND_ID': {
      const { id, backendRequestId } = action.payload;
      const session = state.sessions[id];
      if (!session) return state;
      // If already attached, skip
      if (session.backendRequestId === backendRequestId) return state;
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: { ...session, backendRequestId },
        },
      };
    }

    case 'ADD_ERROR': {
      const { id, error } = action.payload;
      const session = state.sessions[id];
      if (!session) return state;

      const updatedSession = {
        ...session,
        errors: [...session.errors, error],
        status: ExtractionStatus.FAILED,
        endTime: new Date().toISOString(),
      };

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: updatedSession,
        },
        activeSessionId: state.activeSessionId === id ? null : state.activeSessionId,
      };
    }

    case 'CANCEL_EXTRACTION': {
      const { id } = action.payload;
      const session = state.sessions[id];
      if (!session) return state;

      const updatedSession = {
        ...session,
        status: ExtractionStatus.CANCELLED,
        endTime: new Date().toISOString(),
      };

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: updatedSession,
        },
        activeSessionId: state.activeSessionId === id ? null : state.activeSessionId,
      };
    }

    case 'SELECT_SESSION': {
      return {
        ...state,
        selectedSessionId: action.payload.id,
      };
    }

    case 'DELETE_SESSION': {
      const { id } = action.payload;
      const { [id]: deleted, ...remainingSessions } = state.sessions;
      
      return {
        ...state,
        sessions: remainingSessions,
        activeSessionId: state.activeSessionId === id ? null : state.activeSessionId,
        selectedSessionId: state.selectedSessionId === id ? null : state.selectedSessionId,
      };
    }

    case 'UPDATE_CODE_COLORS': {
      const { id, codeColors } = action.payload;
      const session = state.sessions[id];
      if (!session) return state;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: { ...session, codeColors },
        },
      };
    }

    case 'UPDATE_TITLE': {
      const { id, title } = action.payload;
      const session = state.sessions[id];
      if (!session) return state;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: { ...session, title },
        },
      };
    }

    case 'LOAD_PERSISTED_SESSIONS': {
      return {
        ...state,
        sessions: action.payload.sessions,
      };
    }

    default:
      return state;
  }
}

// Helper function to generate a title from text
function generateTitle(text: string): string {
  const preview = text.trim().substring(0, 50);
  return preview.length < text.trim().length ? `${preview}...` : preview;
}

// Helper function to generate stable hue from string hash
function hashToHue(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash << 5) - hash + s.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 360;
}

// Generate unique ID for extraction sessions
function generateSessionId(): string {
  return `extraction_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Local storage key for persistence
const STORAGE_KEY = 'icd10_extraction_sessions';

// Custom hook for managing multiple extractions
export function useMultiExtraction() {
  const [state, dispatch] = useReducer(multiExtractionReducer, initialState);

  // Load persisted sessions on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const sessions = JSON.parse(stored);
        dispatch({ type: 'LOAD_PERSISTED_SESSIONS', payload: { sessions } });
      }
    } catch (error) {
      console.warn('Failed to load persisted extraction sessions:', error);
    }
  }, []);

  // Persist sessions to localStorage whenever state changes
  useEffect(() => {
    try {
      // Only persist completed sessions to avoid losing in-progress data
      const completedSessions = Object.fromEntries(
        Object.entries(state.sessions).filter(([_, session]) => 
          session.status === ExtractionStatus.COMPLETED
        )
      );
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completedSessions));
    } catch (error) {
      console.warn('Failed to persist extraction sessions:', error);
    }
  }, [state.sessions]);

  // Actions
  const startExtraction = useCallback((request: ExtractionRequest, originalText: string, title?: string) => {
    const id = generateSessionId();
    dispatch({ 
      type: 'START_EXTRACTION', 
      payload: { id, request, originalText, title } 
    });
    return id;
  }, []);

  const updateProgress = useCallback((id: string, progress: ExtractionProgressData) => {
    dispatch({ type: 'UPDATE_PROGRESS', payload: { id, progress } });
  }, []);

  const completeExtraction = useCallback((id: string, results: ExtractionResponse) => {
    dispatch({ type: 'COMPLETE_EXTRACTION', payload: { id, results } });
  }, []);

  const attachBackendRequestId = useCallback((id: string, backendRequestId: string) => {
    dispatch({ type: 'ATTACH_BACKEND_ID', payload: { id, backendRequestId } });
  }, []);

  const addError = useCallback((id: string, error: SocketError) => {
    dispatch({ type: 'ADD_ERROR', payload: { id, error } });
  }, []);

  const cancelExtraction = useCallback((id: string) => {
    dispatch({ type: 'CANCEL_EXTRACTION', payload: { id } });
  }, []);

  const selectSession = useCallback((id: string | null) => {
    dispatch({ type: 'SELECT_SESSION', payload: { id } });
  }, []);

  const deleteSession = useCallback((id: string) => {
    dispatch({ type: 'DELETE_SESSION', payload: { id } });
  }, []);

  const updateCodeColors = useCallback((id: string, codeColors: Record<string, string>) => {
    dispatch({ type: 'UPDATE_CODE_COLORS', payload: { id, codeColors } });
  }, []);

  const updateTitle = useCallback((id: string, title: string) => {
    dispatch({ type: 'UPDATE_TITLE', payload: { id, title } });
  }, []);

  // Selectors
  const getSelectedSession = useCallback((): ExtractionSession | null => {
    return state.selectedSessionId ? state.sessions[state.selectedSessionId] || null : null;
  }, [state.selectedSessionId, state.sessions]);

  const getActiveSession = useCallback((): ExtractionSession | null => {
    return state.activeSessionId ? state.sessions[state.activeSessionId] || null : null;
  }, [state.activeSessionId, state.sessions]);

  const getAllSessions = useCallback((): ExtractionSession[] => {
    return Object.values(state.sessions).sort((a, b) => 
      new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
    );
  }, [state.sessions]);

  const findByBackendRequestId = useCallback((backendRequestId: string): ExtractionSession | undefined => {
    return Object.values(state.sessions).find(s => s.backendRequestId === backendRequestId);
  }, [state.sessions]);

  const getInProgressSessions = useCallback((): ExtractionSession[] => {
    return Object.values(state.sessions).filter(session => 
      session.status === ExtractionStatus.IN_PROGRESS
    );
  }, [state.sessions]);

  const getCompletedSessions = useCallback((): ExtractionSession[] => {
    return Object.values(state.sessions).filter(session => 
      session.status === ExtractionStatus.COMPLETED
    );
  }, [state.sessions]);

  // Actions - memoized to prevent unnecessary re-renders
  const actions = useMemo(() => ({
    startExtraction,
    updateProgress,
    completeExtraction,
    attachBackendRequestId,
    addError,
    cancelExtraction,
    selectSession,
    deleteSession,
    updateCodeColors,
    updateTitle,
  }), [startExtraction, updateProgress, completeExtraction, attachBackendRequestId, addError, cancelExtraction, selectSession, deleteSession, updateCodeColors, updateTitle]);

  // Selectors - memoized to prevent unnecessary re-renders
  const selectors = useMemo(() => ({
    getSelectedSession,
    getActiveSession,
    getAllSessions,
    getInProgressSessions,
    getCompletedSessions,
    findByBackendRequestId,
  }), [getSelectedSession, getActiveSession, getAllSessions, getInProgressSessions, getCompletedSessions, findByBackendRequestId]);

  return {
    state,
    actions,
    selectors,
  };
}